import { evalCurve, type Keyframe } from './curve'
import type { Side } from './gait'
import type { PlaneKey } from './joints'

/**
 * Bewegungen, die KEIN Gang sind.
 *
 * Der Unterschied ist nicht kosmetisch. Ein Gang hat eine Schrittlänge, einen
 * Standphasenanteil und zwei Beine, die eine halbe Periode auseinanderlaufen;
 * seine Winkel entstehen aus einer Aufgabe oder aus Normkurven, die genau diese
 * Struktur voraussetzen. Ein Hackensprung hat beide Beine GLEICHPHASIG, ein
 * Aufheben hat gar keine Periodik im Bein — es kniet eines ab.
 *
 * Beide durch die Gangkette zu schicken hiesse, ihnen einen Phasenversatz und
 * eine Schrittlänge zu geben, die sie nicht haben. Sie stehen deshalb hier als
 * eigene Stützstellenskripte, so wie `STAND_POSE` als feste Haltung danebensteht.
 *
 * DIE HÖHENLAGE STEHT NICHT DARIN. Sie folgt wie überall aus dem tiefsten
 * Sohlenpunkt — der Bodenkontakt setzt den Körper auf den Boden, und beim
 * Aufheben legt er dabei das Knie ab. Ein Skript, das die Beckenhöhe selbst
 * vorschriebe, stünde gegen diese Rechnung und würde sie überstimmen.
 *
 * Zahlen aus der Vorlage übernommen, Phasen auf 0 … 1.
 */

/** Eine Winkelbahn über den Zyklus: entweder für beide Seiten oder je Seite. */
export type MoveTrack = Keyframe[] | Record<Side, Keyframe[]>

export interface MoveScript {
  key: string
  label: string
  note: string
  /**
   * Standphasenanteil je Bein, 0 … 1.
   *
   * Beim Hackensprung sind beide Beine gleichzeitig in der Luft; der Anteil ist
   * deshalb für beide derselbe und nicht, wie im Gang, um eine halbe Periode
   * versetzt. Beim Aufheben tragen beide durchgehend.
   */
  stance: number
  /**
   * Wo im Skript der Bodenkontakt beginnt, als Phasenanteil.
   *
   * DER BODENKONTAKT MUSS BEI PHASE NULL ANFANGEN — darauf baut die ganze
   * Kontaktrechnung auf, beim Gang wie hier. Die Vorlage schreibt den
   * Hackensprung aber ab der Hocke: die Flugphase liegt bei ihr in der Mitte,
   * von 0,34 bis 0,66, und der Aufsetzpunkt damit bei 0,66.
   *
   * Statt die Stützstellen umzuschreiben — und damit die Vergleichbarkeit mit
   * der Vorlage zu verlieren — wird die Zeitachse hier gedreht. Modellphase 0
   * ist Skriptphase 0,66: das Aufsetzen. Was danach kommt, ist Abfedern,
   * Hocke, Abdruck, Flug — in dieser Reihenfolge und ohne Sprung an der
   * Zyklusgrenze.
   */
  contactStart: number
  /** Winkel je Gelenk und Ebene. Was fehlt, ist null. */
  angles: Record<string, Partial<Record<PlaneKey, MoveTrack>>>
}

const track = (t: MoveTrack, side: Side): Keyframe[] => (Array.isArray(t) ? t : t[side])

/**
 * Stützstellen von der Phase 0 … 1 in Prozent.
 *
 * Die Tabellen unten sind in der Phase geschrieben, weil sie so auch in der
 * Vorlage stehen und sich Zeile für Zeile dagegen prüfen lassen. `evalCurve`
 * rechnet dagegen in Prozent — wie alle Normkurven des Gangs. Die Umrechnung
 * steht deshalb hier, EINMAL und beim Anlegen, und nicht bei jedem Abruf.
 */
const cycle = (rows: readonly (readonly [number, number])[]): Keyframe[] =>
  rows.map(([phase, value]) => [phase * 100, value] as Keyframe)

/* -------------------------------------------------------------------------
   Hackensprung
   ------------------------------------------------------------------------- */

/** Anfang und Ende der Flugphase, als Phasenanteil. */
export const HOP_FLIGHT = { from: 0.34, to: 0.66 } as const

/**
 * Hackensprung — Hocke, Abdruck, Hacken zusammen, abfedern.
 *
 * DIE ZWEI FRONTALWERTE BEI 0,42 UND 0,58 SIND EIN BEFUND UND KEINE WAHL. Es
 * sind die Momente, in denen die Hacken zusammenkommen. Bei 8° berühren sich
 * zwar die Fersen, die Schienbeine stehen dabei aber nur 79 mm auseinander und
 * durchdringen einander — sie sind zusammen rund 94 mm dick. Bei 6,5° bleiben
 * 111 mm zwischen den Schienbeinen, und die Fersen kommen trotzdem zur
 * Berührung. Wer die Zahl auf den anschaulicheren Wert zurückstellt, bekommt
 * eine Bewegung, die im Bild richtig aussieht und in der Freigangsprüfung
 * durchfällt.
 */
export const HOP: MoveScript = {
  key: 'hop',
  label: 'Hackensprung',
  note: 'Beide Beine gleichphasig: Hocke, Abdruck, im Flug die Hacken zusammenschlagen, abfedern.',
  stance: 1 - (HOP_FLIGHT.to - HOP_FLIGHT.from),
  contactStart: HOP_FLIGHT.to,
  angles: {
    hip: {
      sag: cycle([
        [0, 3],
        [0.03, 3],
        [0.1, 30],
        [0.22, 56],
        [0.28, 36],
        [0.34, 4],
        [0.4, 36],
        [0.5, 66],
        [0.6, 36],
        [0.66, 6],
        [0.73, 40],
        [0.81, 60],
        [0.9, 22],
        [0.96, 6],
        [1, 3],
      ]),
      fro: cycle([
        [0, 0],
        [0.34, 0],
        [0.38, -14],
        [0.42, 6.5],
        [0.46, -22],
        [0.5, -22],
        [0.54, -22],
        [0.58, 6.5],
        [0.62, -10],
        [0.66, 0],
        [1, 0],
      ]),
    },
    knee: {
      sag: cycle([
        [0, 6],
        [0.03, 6],
        [0.1, 44],
        [0.22, 90],
        [0.28, 56],
        [0.34, 8],
        [0.4, 48],
        [0.5, 130],
        [0.6, 48],
        [0.66, 12],
        [0.73, 72],
        [0.81, 108],
        [0.9, 32],
        [0.96, 10],
        [1, 6],
      ]),
    },
    ankle: {
      sag: cycle([
        [0, 0],
        [0.03, 0],
        [0.1, 12],
        [0.22, 26],
        [0.28, 10],
        [0.34, -30],
        [0.4, -16],
        [0.5, -8],
        [0.6, -16],
        [0.66, -16],
        [0.71, 16],
        [0.81, 28],
        [0.9, 14],
        [0.96, 2],
        [1, 0],
      ]),
    },
    mtp: {
      sag: cycle([
        [0, 0],
        [0.22, 6],
        [0.3, 18],
        [0.34, 26],
        [0.42, 0],
        [1, 0],
      ]),
    },
    shoulder: {
      sag: cycle([
        [0, 8],
        [0.03, 8],
        [0.1, -24],
        [0.22, -48],
        [0.28, 20],
        [0.34, 120],
        [0.45, 150],
        [0.58, 140],
        [0.66, 70],
        [0.81, -28],
        [0.93, 8],
        [1, 8],
      ]),
      fro: cycle([
        [0, 4],
        [0.34, 10],
        [0.45, 26],
        [0.55, 28],
        [0.66, 14],
        [0.86, 4],
        [1, 4],
      ]),
    },
    elbow: {
      sag: cycle([
        [0, 14],
        [0.22, 34],
        [0.34, 20],
        [0.45, 76],
        [0.58, 80],
        [0.66, 48],
        [0.81, 28],
        [1, 14],
      ]),
    },
    pelvis: {
      sag: cycle([
        [0, 0],
        [0.22, 11],
        [0.3, 7],
        [0.34, 2],
        [0.5, -4],
        [0.66, 1],
        [0.81, 10],
        [0.93, 2],
        [1, 0],
      ]),
    },
    lumbar: {
      sag: cycle([
        [0, 0],
        [0.22, 7],
        [0.34, 0],
        [0.5, -5],
        [0.66, 0],
        [0.81, 6],
        [1, 0],
      ]),
    },
  },
}

/* -------------------------------------------------------------------------
   Aufheben
   ------------------------------------------------------------------------- */

/**
 * Aufheben — von stehend nach stehend.
 *
 * KEIN GANGBILD, SONDERN EINE HANDLUNG: absenken in den Kniestand, greifen,
 * aufrichten. Beide Füsse zählen durchgehend als Standbein, es gibt keine
 * Schwungphase. Die Höhenlage folgt wie immer aus dem tiefsten Sohlenpunkt; die
 * Winkel sind so gesetzt, dass das Knie dabei auf dem Boden liegt.
 *
 * Die Frontalanteile sind nicht Zierde: ohne sie stossen die Knie in der tiefen
 * Hocke gegen den Rumpf.
 *
 * DIE BEIDEN SEITEN SIND LEICHT VERSCHIEDEN und nicht spiegelgleich — die eine
 * kniet ab, die andere bleibt flach vorn stehen. Ein Skript mit nur einer Bahn
 * ergäbe zwei Beine in derselben Stellung und damit eine Hocke statt eines
 * Kniestands.
 */
export const PICK: MoveScript = {
  key: 'pick',
  label: 'Aufheben',
  note: 'Absenken in den Kniestand, greifen, aufrichten. Beide Füsse tragen durchgehend.',
  stance: 1,
  /* Durchgehender Kontakt — es gibt keinen Aufsetzpunkt zu treffen. */
  contactStart: 0,
  angles: {
    hip: {
      sag: {
        R: cycle([
          [0, -2],
          [0.08, -2],
          [0.2, 30],
          [0.34, 62],
          [0.44, 84],
          [0.5, 90],
          [0.58, 86],
          [0.7, 56],
          [0.84, 18],
          [0.94, -2],
          [1, -2],
        ]),
        L: cycle([
          [0, -2],
          [0.08, -2],
          [0.2, 28],
          [0.34, 60],
          [0.44, 82],
          [0.5, 88],
          [0.58, 84],
          [0.7, 54],
          [0.84, 16],
          [0.94, -2],
          [1, -2],
        ]),
      },
      fro: {
        R: cycle([
          [0, -1],
          [0.2, -6],
          [0.5, -11],
          [0.8, -4],
          [1, -1],
        ]),
        L: cycle([
          [0, -1],
          [0.2, -6],
          [0.5, -11],
          [0.8, -4],
          [1, -1],
        ]),
      },
    },
    knee: {
      sag: {
        R: cycle([
          [0, 4],
          [0.08, 4],
          [0.2, 46],
          [0.34, 86],
          [0.44, 116],
          [0.5, 124],
          [0.58, 120],
          [0.7, 74],
          [0.84, 26],
          [0.94, 4],
          [1, 4],
        ]),
        L: cycle([
          [0, 4],
          [0.08, 4],
          [0.2, 44],
          [0.34, 84],
          [0.44, 114],
          [0.5, 122],
          [0.58, 118],
          [0.7, 72],
          [0.84, 24],
          [0.94, 4],
          [1, 4],
        ]),
      },
    },
    ankle: {
      sag: {
        R: cycle([
          [0, 7.2],
          [0.08, 7.2],
          [0.2, 16],
          [0.34, 26],
          [0.44, 33],
          [0.5, 35],
          [0.58, 34],
          [0.7, 22],
          [0.84, 11],
          [0.94, 7.2],
          [1, 7.2],
        ]),
        L: cycle([
          [0, 7.2],
          [0.08, 7.2],
          [0.2, 15],
          [0.34, 25],
          [0.44, 32],
          [0.5, 34],
          [0.58, 33],
          [0.7, 21],
          [0.84, 11],
          [0.94, 7.2],
          [1, 7.2],
        ]),
      },
    },
    pelvis: {
      sag: cycle([
        [0, -3],
        [0.08, -3],
        [0.2, 4],
        [0.34, 11],
        [0.44, 15],
        [0.5, 16],
        [0.58, 14],
        [0.7, 8],
        [0.84, 0],
        [0.94, -3],
        [1, -3],
      ]),
    },
    lumbar: {
      sag: cycle([
        [0, 0],
        [0.08, 0],
        [0.2, 8],
        [0.34, 16],
        [0.44, 21],
        [0.5, 22],
        [0.58, 17],
        [0.7, 9],
        [0.84, 2],
        [0.94, 0],
        [1, 0],
      ]),
    },
    thorax: {
      sag: cycle([
        [0, 0],
        [0.2, 3],
        [0.44, 7],
        [0.5, 8],
        [0.58, 6],
        [0.84, 1],
        [1, 0],
      ]),
    },
    shoulder: {
      sag: {
        R: cycle([
          [0, -3],
          [0.08, -3],
          [0.2, 0],
          [0.34, 4],
          [0.44, 8],
          [0.5, 10],
          [0.58, 9],
          [0.7, 6],
          [0.84, 1],
          [0.94, -3],
          [1, -3],
        ]),
        L: cycle([
          [0, -3],
          [0.08, -3],
          [0.2, 0],
          [0.34, 3],
          [0.44, 6],
          [0.5, 8],
          [0.58, 7],
          [0.7, 5],
          [0.84, 1],
          [0.94, -3],
          [1, -3],
        ]),
      },
    },
    elbow: {
      sag: {
        R: cycle([
          [0, 12],
          [0.08, 12],
          [0.2, 10],
          [0.34, 7],
          [0.44, 5],
          [0.5, 4],
          [0.58, 30],
          [0.7, 58],
          [0.84, 72],
          [0.94, 70],
          [1, 12],
        ]),
        L: cycle([
          [0, 12],
          [0.08, 12],
          [0.2, 10],
          [0.34, 8],
          [0.44, 6],
          [0.5, 6],
          [0.58, 32],
          [0.7, 60],
          [0.84, 74],
          [0.94, 70],
          [1, 12],
        ]),
      },
    },
  },
}

export const MOVES: Record<string, MoveScript> = { hop: HOP, pick: PICK }

/** Ist diese Gangart eine Bewegung aus einem Skript und kein Gang? */
export const isMove = (kind: string): boolean => kind in MOVES

/**
 * Der Winkel einer Skriptbewegung, Grad.
 *
 * KEIN PHASENVERSATZ ZWISCHEN DEN SEITEN. Das ist der eine Unterschied zur
 * Gangkette, und er ist der Grund, warum diese Funktion existiert: beim
 * Hackensprung springen beide Beine zugleich, beim Aufheben kniet eines ab.
 * Beide Male wäre eine um eine halbe Periode versetzte Gegenseite falsch.
 */
export function moveAngle(
  kind: string,
  joint: string,
  plane: PlaneKey,
  side: Side,
  t: number,
): number {
  const script = MOVES[kind]
  const entry = script?.angles[joint]?.[plane]
  if (!entry) return 0
  const rows = track(entry, side)
  /* Die Zeitachse gedreht: Modellphase 0 ist der Aufsetzpunkt des Skripts. */
  const phase = t + script.contactStart
  /* `evalCurve` rechnet in Prozent, wie bei allen Normkurven des Gangs — die
     Stützstellen wurden beim Anlegen umgerechnet (siehe `cycle`). */
  return evalCurve(rows, (((phase % 1) + 1) % 1) * 100)
}

/**
 * Laufen die beiden Beine GLEICHPHASIG?
 *
 * DIE FRAGE, DIE DIE HALBE KONTAKTRECHNUNG ENTSCHEIDET. Ein Gang hat zwei
 * gleiche Halbzyklen: das linke Bein macht eine halbe Periode später, was das
 * rechte gerade macht. Daraus folgt, dass ein Zyklus zwei Standphasen enthält
 * und dass beide Beine zusammen `2 × Standphasenanteil` des Zyklus tragen.
 *
 * Beim Hackensprung stimmt davon nichts. Beide Beine springen zugleich; der
 * Zyklus hat EINEN Abschnitt, und die Tragzeit ist der Standphasenanteil selbst.
 * Wer die Gangannahme stehen lässt, rechnet für den Sprung `2 × 0,68 = 1,36`
 * Tragzeit, kommt über eins und bekommt eine Bewegung, die den Boden nie
 * verlässt — genau so sah der erste Durchlauf aus: Bodenkraft 1,01 Körpergewicht
 * bei einem Sprung.
 */
export const inPhase = (kind: string): boolean => isMove(kind)

/**
 * Wie viele gleiche Abschnitte ein Zyklus hat.
 *
 * Zwei beim Gang — die beiden Schritte —, einer bei gleichphasigen Beinen.
 */
export const cycleSteps = (kind: string): number => (inPhase(kind) ? 1 : 2)
